import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useGetEvalRunDetailQuery } from "../../../app/api/console/evalResultsApiSlice";
import HistoryChart from "./HistoryChart";
import { VerdictBadge, CellsBar } from "./index";
import {
  RUN_KIND_LABELS,
  FAMILY_LABELS,
  formatMetric,
  formatUsd,
  formatDate,
  shortSha,
} from "./evalConfig";

function MetaItem({ label, mono = false, children }) {
  return (
    <div>
      <div className='text-[11px] uppercase tracking-wide text-gray-400 font-semibold'>
        {label}
      </div>
      <div
        className={`text-sm text-gray-800 mt-0.5 break-all ${mono ? "font-mono text-xs" : ""}`}>
        {children ?? "—"}
      </div>
    </div>
  );
}

function FailReasons({ reasons }) {
  if (!reasons) return null;
  const list = Array.isArray(reasons) ? reasons : [reasons];
  if (!list.length) return null;
  return (
    <ul className='space-y-1'>
      {list.map((r, i) => {
        if (typeof r === "string") {
          return (
            <li key={i} className='text-xs text-red-700'>
              {r}
            </li>
          );
        }
        return (
          <li key={i} className='text-xs'>
            <span className='font-mono font-semibold text-red-700'>
              {r.check || r.id || "check"}
            </span>
            {": "}
            <span className='text-gray-700'>{r.reason || JSON.stringify(r)}</span>
          </li>
        );
      })}
    </ul>
  );
}

function CellRow({ cell }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetails =
    (cell.fail_reasons && (!Array.isArray(cell.fail_reasons) || cell.fail_reasons.length > 0)) ||
    cell.stop_reason ||
    cell.trace_uri;
  return (
    <>
      <tr
        onClick={() => hasDetails && setExpanded((v) => !v)}
        className={`border-b border-gray-100 last:border-0 ${
          hasDetails ? "hover:bg-gray-50 cursor-pointer" : ""
        }`}>
        <td className='px-4 py-2'>
          <span className='font-mono text-xs text-gray-800'>{cell.cell_id || cell.cell_key}</span>
          {Number(cell.repeat_idx) > 0 && (
            <span className='ml-1 text-[11px] text-gray-400'>r{cell.repeat_idx}</span>
          )}
        </td>
        <td className='px-4 py-2 text-gray-600 whitespace-nowrap'>
          {FAMILY_LABELS[cell.family] || cell.family || "—"}
        </td>
        <td className='px-4 py-2 text-gray-600 whitespace-nowrap'>{cell.polarity || "—"}</td>
        <td className='px-4 py-2'>
          <VerdictBadge verdict={cell.verdict} />
        </td>
        <td className='px-4 py-2 text-gray-700 tabular-nums'>{formatMetric(cell.precision)}</td>
        <td className='px-4 py-2 text-gray-700 tabular-nums'>{formatMetric(cell.recall)}</td>
        <td className='px-4 py-2 text-gray-600 whitespace-nowrap'>{formatUsd(cell.cost_usd)}</td>
        <td className='px-4 py-2 text-right'>
          {hasDetails && (
            <svg
              className={`inline w-3.5 h-3.5 text-gray-400 transition-transform ${
                expanded ? "rotate-180" : ""
              }`}
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M19.5 8.25l-7.5 7.5-7.5-7.5' />
            </svg>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className='bg-gray-50/70 border-b border-gray-100'>
          <td colSpan={8} className='px-6 py-3'>
            <div className='space-y-2'>
              {cell.fail_reasons && (
                <div>
                  <div className='text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1'>
                    Fail reasons
                  </div>
                  <FailReasons reasons={cell.fail_reasons} />
                </div>
              )}
              <div className='flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500'>
                {cell.case_id && (
                  <span>
                    case <span className='font-mono text-gray-700'>{cell.case_id}</span>
                  </span>
                )}
                {cell.stop_reason && (
                  <span>
                    stop <span className='font-mono text-gray-700'>{cell.stop_reason}</span>
                  </span>
                )}
                {cell.tool_calls !== undefined && cell.tool_calls !== null && (
                  <span>
                    tool calls <span className='text-gray-700'>{cell.tool_calls}</span>
                  </span>
                )}
                {cell.wallclock_seconds !== undefined && cell.wallclock_seconds !== null && (
                  <span>
                    wallclock <span className='text-gray-700'>{cell.wallclock_seconds}s</span>
                  </span>
                )}
                {cell.trace_uri && (
                  <span className='font-mono text-gray-600 break-all'>{cell.trace_uri}</span>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function EvalRunDetail() {
  const { runId } = useParams();
  const { data, isLoading, error } = useGetEvalRunDetailQuery(runId);

  const run = data?.run;
  const cells = data?.cells || [];

  return (
    <div className='w-full max-w-none'>
      {/* Header */}
      <div className='flex items-start justify-between mb-5'>
        <div>
          <Link
            to='/admin/eval-results'
            className='text-xs text-indigo-600 hover:text-indigo-800'>
            ← Eval Results
          </Link>
          <h1 className='text-xl font-semibold text-gray-900 mt-1 font-mono'>{runId}</h1>
          {run && (
            <p className='text-sm text-gray-500 mt-1'>
              {run.suite_id || "—"} · {RUN_KIND_LABELS[run.run_kind] || run.run_kind}
              {run.gate_label ? ` · ${run.gate_label}` : ""} · {formatDate(run.started_at)}
            </p>
          )}
        </div>
        {run && <VerdictBadge verdict={run.verdict} />}
      </div>

      {error && (
        <div className='mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700'>
          {error?.status === 404
            ? `Run "${runId}" was not found in the eval store.`
            : `Failed to load run: ${error?.data?.detail || error?.error || "unknown error"}`}
        </div>
      )}
      {isLoading && <div className='px-4 py-10 text-center text-gray-400'>Loading run…</div>}

      {run && (
        <>
          {/* Aggregates strip */}
          <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4'>
            {[
              ["Median TCQ", formatMetric(run.median_tcq)],
              ["Median precision", formatMetric(run.median_precision)],
              ["Median recall", formatMetric(run.median_recall)],
              ["Cells", `${run.cells_passed ?? 0} / ${(run.cells_passed ?? 0) + (run.cells_failed ?? 0)}`],
              ["Cost", formatUsd(run.total_cost_usd)],
              ["Repeats", run.repeats ?? 1],
            ].map(([label, value]) => (
              <div key={label} className='bg-white border border-gray-200 rounded-xl px-4 py-3'>
                <div className='text-[11px] uppercase tracking-wide text-gray-400 font-semibold'>
                  {label}
                </div>
                <div className='text-lg font-semibold text-gray-900 tabular-nums mt-0.5'>
                  {value}
                </div>
              </div>
            ))}
          </div>

          {/* Provenance tuple */}
          <div className='bg-white border border-gray-200 rounded-xl p-4 mb-4'>
            <h2 className='text-sm font-semibold text-gray-900 mb-3'>Provenance</h2>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4'>
              <MetaItem label='Agent type'>{run.agent_type}</MetaItem>
              <MetaItem label='Brand'>{run.brand}</MetaItem>
              <MetaItem label='Website' mono>{run.website_id}</MetaItem>
              <MetaItem label='Environment'>{run.environment}</MetaItem>
              <MetaItem label='Skill SHA' mono>{shortSha(run.skill_sha, 12)}</MetaItem>
              <MetaItem label='Memory SHA' mono>
                {shortSha(run.memory_sha, 12)}
                {run.memory_source ? ` (${run.memory_source})` : ""}
              </MetaItem>
              <MetaItem label='Pack'>
                {run.pack_name ? `${run.pack_name} ` : ""}
                <span className='font-mono text-xs'>{run.pack_version || "—"}</span>
              </MetaItem>
              <MetaItem label='Manifest' mono>
                {run.manifest_version || shortSha(run.manifest_sha256, 12)}
                {run.gt_rows ? ` · ${run.gt_rows} GT rows` : ""}
              </MetaItem>
              <MetaItem label='Model' mono>
                {run.model_id}
                {run.provider ? ` (${run.provider})` : ""}
              </MetaItem>
              <MetaItem label='Services SHA' mono>
                {shortSha(run.services_git_sha, 12)}
                {run.dirty ? " · dirty" : ""}
              </MetaItem>
              <MetaItem label='Ticket'>{run.ticket}</MetaItem>
              <MetaItem label='Operator'>{run.operator}</MetaItem>
            </div>
            {run.provenance_incomplete && (
              <div className='mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2'>
                Provenance tuple incomplete — some version fields were unknown at
                push time.
              </div>
            )}
          </div>

          {/* History for this run's website + agent */}
          {run.website_id && run.agent_type && (
            <div className='bg-white border border-gray-200 rounded-xl p-4 mb-4'>
              <h2 className='text-sm font-semibold text-gray-900 mb-2'>
                Metric history
                <span className='ml-2 font-normal text-gray-500'>
                  {run.brand ? `${run.brand} · ` : ""}
                  {run.agent_type}
                </span>
              </h2>
              <HistoryChart websiteId={run.website_id} agentType={run.agent_type} />
            </div>
          )}

          {/* Per-cell table */}
          <div className='bg-white border border-gray-200 rounded-xl overflow-hidden'>
            <div className='px-4 py-3 border-b border-gray-200 flex items-center justify-between'>
              <h2 className='text-sm font-semibold text-gray-900'>
                Cells{" "}
                <span className='font-normal text-gray-400'>({cells.length})</span>
              </h2>
              <CellsBar passed={run.cells_passed} failed={run.cells_failed} />
            </div>
            <table className='w-full text-sm'>
              <thead>
                <tr className='bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200'>
                  <th className='px-4 py-2.5 font-semibold'>Cell</th>
                  <th className='px-4 py-2.5 font-semibold'>Family</th>
                  <th className='px-4 py-2.5 font-semibold'>Polarity</th>
                  <th className='px-4 py-2.5 font-semibold'>Verdict</th>
                  <th className='px-4 py-2.5 font-semibold'>P</th>
                  <th className='px-4 py-2.5 font-semibold'>R</th>
                  <th className='px-4 py-2.5 font-semibold'>Cost</th>
                  <th className='px-4 py-2.5'></th>
                </tr>
              </thead>
              <tbody>
                {cells.length === 0 ? (
                  <tr>
                    <td colSpan={8} className='px-4 py-8 text-center text-gray-400'>
                      {data?.cells_table_missing
                        ? "Cells table not provisioned yet."
                        : "No cells recorded for this run."}
                    </td>
                  </tr>
                ) : (
                  cells.map((c) => (
                    <CellRow key={`${c.cell_key || c.cell_id}-${c.repeat_idx}`} cell={c} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default EvalRunDetail;
